import { useState } from 'react';
import { Button, Field, MetaGrid, MetaItem, Screen, ScreenHeader, Select, TierBadge } from '@garnet/ui';
import type { TpmTier, VerticalData } from '../data/types.js';
import { suggestTier } from '../data/shape.js';

/* Garnet · TPM classification panel */

const TIERS: TpmTier[] = ['low', 'standard', 'high', 'critical'];
const tierFromSupplier: Record<TpmTier, number[]> = {
  low: [1, 1, 1, 1, 1],
  standard: [2, 1, 2, 1, 1],
  high: [3, 2, 3, 2, 2],
  critical: [3, 3, 3, 3, 2],
};

interface Override {
  tier: TpmTier;
  note: string;
  by: string;
  at: string;
}
interface HistEntry {
  tier: TpmTier;
  why: string;
  by: string;
  at: string;
}

function LevelPick({ value, onChange }: { value: number; onChange: (n: number) => void }) {
  const labels = ['low', 'med', 'high'];
  return (
    <div
      style={{
        display: 'inline-flex',
        border: '1px solid var(--rule)',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
      }}
    >
      {[1, 2, 3].map((n) => (
        <button
          key={n}
          onClick={() => onChange(n)}
          aria-pressed={value === n}
          style={{
            fontFamily: 'var(--mono)',
            fontSize: 10.5,
            fontWeight: 600,
            letterSpacing: '.03em',
            padding: '5px 10px',
            border: '0',
            borderRight: n < 3 ? '1px solid var(--rule)' : 0,
            cursor: 'pointer',
            background: value === n ? 'var(--ink)' : 'var(--panel)',
            color: value === n ? 'var(--paper)' : 'var(--muted)',
          }}
        >
          {labels[n - 1]}
        </button>
      ))}
    </div>
  );
}

export function Tpm({ data }: { data: VerticalData }) {
  const { suppliers, tpmInputs, tierDrives } = data;
  const first = suppliers[0];
  const [supplierId, setSupplierId] = useState<string>(first?.id ?? '');
  const s = suppliers.find((x) => x.id === supplierId) ?? first;
  const [levels, setLevels] = useState<number[]>(() =>
    first ? [...tierFromSupplier[first.tier]] : [2, 2, 2, 2, 2],
  );
  const [override, setOverride] = useState<Override | null>(null);
  const [pendingTier, setPendingTier] = useState<TpmTier>(first?.tier ?? 'low');
  const [rationale, setRationale] = useState('');
  const [lastVerifiedStr, setLastVerifiedStr] = useState('2026-06-09');
  const [justReverified, setJustReverified] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [schedInput, setSchedInput] = useState('2026-07-09');
  const [scheduledFor, setScheduledFor] = useState<string | null>(null);

  if (!s) return null;

  const sum = levels.reduce((a, b) => a + b, 0);
  const suggested = suggestTier(sum);
  const effective = override ? override.tier : suggested;
  const drives = tierDrives[effective];

  // monitoring schedule derived from cadence
  const cadMap: Record<string, number> = { daily: 1, weekly: 7, monthly: 30, quarterly: 90 };
  const cadDays = cadMap[drives.cadence] ?? 30;
  const lastVerified = new Date(lastVerifiedStr);
  const due = new Date(lastVerified.getTime() + cadDays * 86400000);
  const today = new Date('2026-06-11');
  const dueStr = due.toISOString().slice(0, 10);
  const effDueStr = scheduledFor ?? dueStr;
  const effDue = new Date(effDueStr);
  const effDays = Math.round((effDue.getTime() - today.getTime()) / 86400000);
  const effPill: [string, string] =
    effDays < 0 ? ['f', 'overdue'] : effDays <= 2 ? ['a', 'due soon'] : ['m', 'on track'];
  const reqDepth = drives.depth;
  const achievedDepth = s.state === 'clear' ? reqDepth : 'identity-authentic';
  const depthMet = achievedDepth === reqDepth;
  const TRIGGERS = [
    'Recall or field-safety action published on a monitored part',
    'Certificate or type-approval status change at the issuing body',
    'A source goes stale beyond its freshness window',
    'Ledger duplicate or diversion hit on a unit',
    'A risk-input change re-scores the tier',
  ];
  const history: HistEntry[] = [];
  if (override) history.push({ tier: override.tier, why: 'manual override', by: override.by, at: override.at });
  history.push({ tier: s.tier, why: 'risk review · inputs updated', by: 'R. Okonkwo · reviewer', at: '2026-04-03' });
  history.push({ tier: s.tier, why: 'tier set at onboarding', by: 'S. Okonkwo · operator', at: '2026-01-12' });

  const onSupplier = (id: string) => {
    const ns = suppliers.find((x) => x.id === id);
    setSupplierId(id);
    setLevels(ns ? [...tierFromSupplier[ns.tier]] : [2, 2, 2, 2, 2]);
    setOverride(null);
    setPendingTier(ns?.tier ?? 'low');
    setRationale('');
    setLastVerifiedStr('2026-06-09');
    setJustReverified(false);
    setScheduleOpen(false);
    setScheduledFor(null);
  };
  const reverifyNow = () => {
    setLastVerifiedStr('2026-06-11');
    setScheduledFor(null);
    setJustReverified(true);
  };
  const setLevel = (i: number, v: number) =>
    setLevels((ls) => ls.map((x, j) => (j === i ? v : x)));
  const applyOverride = () =>
    setOverride({ tier: pendingTier, note: rationale, by: 'S. Lench · compliance approver', at: '2026-06-11' });

  const driveRows: Array<[string, string]> = [
    ['diligence depth', drives.depth],
    ['monitoring cadence', drives.cadence],
    ['renewal lead', drives.lead],
    ['approval', drives.approval],
  ];
  const schedRows: Array<[string, string]> = [
    ['cadence', drives.cadence],
    ['last verified', lastVerifiedStr + (justReverified ? ' · just now' : '')],
    [
      'next re-verification',
      effDueStr +
        ' · ' +
        (effDays < 0 ? Math.abs(effDays) + 'd overdue' : 'in ' + effDays + 'd') +
        (scheduledFor ? ' · scheduled' : ''),
    ],
  ];

  return (
    <Screen>
      <ScreenHeader
        eyebrow="§ TPM classification"
        title="Third-party management tier"
        lead="The tier decides how deep each verification goes and how often it repeats. Criticality is structure, not alarm — it never reads as a flag."
      />

      <div style={{ maxWidth: 360 }}>
        <Field label="Supplier">
          <Select
            value={supplierId}
            onChange={onSupplier}
            ariaLabel="Supplier"
            options={suppliers.map((x) => ({ value: x.id, label: x.name }))}
          />
        </Field>
      </div>

      <div className="split-pane">
        {/* risk inputs */}
        <div className="panel" style={{ padding: '20px 22px' }}>
          <div
            style={{
              fontFamily: 'var(--mono)',
              fontSize: 10.5,
              letterSpacing: '.08em',
              textTransform: 'uppercase',
              color: 'var(--muted)',
              marginBottom: 14,
            }}
          >
            Risk inputs
          </div>
          {tpmInputs.map((inp, i) => (
            <div
              key={inp.k}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 14,
                padding: '11px 0',
                borderBottom: i < tpmInputs.length - 1 ? '1px solid var(--rule)' : 0,
              }}
            >
              <div>
                <div style={{ fontSize: 13.5, fontWeight: 500 }}>{inp.k}</div>
                <div
                  style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 2, maxWidth: '34ch' }}
                >
                  {inp.help}
                </div>
              </div>
              <LevelPick value={levels[i] ?? 2} onChange={(v) => setLevel(i, v)} />
            </div>
          ))}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: 16,
              paddingTop: 14,
              borderTop: '1px solid var(--ink)',
            }}
          >
            <span
              style={{
                fontFamily: 'var(--mono)',
                fontSize: 11,
                letterSpacing: '.06em',
                textTransform: 'uppercase',
                color: 'var(--muted)',
              }}
            >
              Suggested tier · score {sum}/15
            </span>
            <TierBadge level={suggested} />
          </div>
        </div>

        {/* drives + override */}
        <div style={{ display: 'grid', gap: 16 }}>
          <div className="panel">
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '14px 18px',
                borderBottom: '1px solid var(--ink)',
              }}
            >
              <span
                style={{
                  fontFamily: 'var(--mono)',
                  fontSize: 10.5,
                  letterSpacing: '.08em',
                  textTransform: 'uppercase',
                  color: 'var(--muted)',
                }}
              >
                Effective tier drives
              </span>
              <TierBadge level={effective} />
            </div>
            <MetaGrid labelWidth={150} style={{ padding: '4px 18px 8px' }}>
              {driveRows.map(([k, v]) => (
                <MetaItem key={k} label={k}>
                  {v}
                </MetaItem>
              ))}
            </MetaGrid>
          </div>

          <div className="panel" style={{ padding: '18px' }}>
            <div
              style={{
                fontFamily: 'var(--mono)',
                fontSize: 10.5,
                letterSpacing: '.08em',
                textTransform: 'uppercase',
                color: 'var(--muted)',
                marginBottom: 12,
              }}
            >
              Override with recorded rationale
            </div>
            {override ? (
              <div className="weak" style={{ borderLeftColor: 'var(--ink)', background: 'var(--rule-soft)' }}>
                <span className="wl" style={{ color: 'var(--ink)' }}>
                  over
                  <br />
                  ride
                </span>
                <span className="wt">
                  Tier set to <b>{override.tier}</b> · {override.by} · {override.at}
                  <br />
                  {override.note}
                  <div style={{ marginTop: 8 }}>
                    <Button sm variant="ghost" icon="refresh" onClick={() => setOverride(null)}>
                      Revert to suggested
                    </Button>
                  </div>
                </span>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: 12 }}>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {TIERS.map((t) => (
                    <button
                      key={t}
                      onClick={() => setPendingTier(t)}
                      aria-pressed={pendingTier === t}
                      style={{
                        cursor: 'pointer',
                        border: '1px solid ' + (pendingTier === t ? 'var(--ink)' : 'var(--rule)'),
                        background: pendingTier === t ? 'var(--paper)' : 'var(--panel)',
                        borderRadius: 'var(--radius-md)',
                        padding: '6px 8px',
                      }}
                    >
                      <TierBadge level={t} sm />
                    </button>
                  ))}
                </div>
                <Field label="Rationale">
                  <textarea
                    className="input"
                    rows={2}
                    value={rationale}
                    onChange={(e) => setRationale(e.target.value)}
                    placeholder="Why this tier differs from the suggested score."
                    style={{ resize: 'vertical', fontFamily: 'var(--body)' }}
                  />
                </Field>
                <Button
                  variant="primary"
                  icon="check"
                  disabled={rationale.trim().length < 8}
                  onClick={applyOverride}
                >
                  Apply override · set tier to {pendingTier}
                </Button>
                {rationale.trim().length < 8 ? (
                  <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)' }}>
                    add a rationale to record the override
                  </div>
                ) : (
                  pendingTier === suggested && (
                    <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)' }}>
                      records the tier even though it matches the suggested score
                    </div>
                  )
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* monitoring + triggers + history */}
      <div className="split-pane">
        <div className="panel" style={{ padding: '20px 22px' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 14,
            }}
          >
            <span
              style={{
                fontFamily: 'var(--mono)',
                fontSize: 10.5,
                letterSpacing: '.08em',
                textTransform: 'uppercase',
                color: 'var(--muted)',
              }}
            >
              Monitoring schedule
            </span>
            <span className={'pill ' + effPill[0]}>{justReverified ? 're-verified' : effPill[1]}</span>
          </div>
          <MetaGrid labelWidth={150}>
            {schedRows.map(([k, v]) => (
              <MetaItem key={k} label={k}>
                {v}
              </MetaItem>
            ))}
            <MetaItem label="depth · required">
              <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 13 }}>{reqDepth}</span>
                <span className={'pill ' + (depthMet ? 'm' : 'a')}>
                  {depthMet ? 'reached' : 'below · at ' + achievedDepth}
                </span>
              </span>
            </MetaItem>
          </MetaGrid>
          <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
            <Button sm variant="primary" icon="refresh" onClick={reverifyNow}>
              Re-verify now
            </Button>
            <Button sm variant="secondary" icon="clock" onClick={() => setScheduleOpen((o) => !o)}>
              Schedule
            </Button>
          </div>
          {justReverified && (
            <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--verified-ink)', marginTop: 8 }}>
              re-verification run · next due {effDueStr}
            </div>
          )}
          {scheduleOpen && (
            <div
              style={{
                marginTop: 12,
                paddingTop: 12,
                borderTop: '1px solid var(--rule)',
                display: 'grid',
                gap: 10,
              }}
            >
              <Field label="Schedule next re-verification">
                <input
                  className="input mono"
                  type="date"
                  value={schedInput}
                  onChange={(e) => setSchedInput(e.target.value)}
                />
              </Field>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button
                  sm
                  variant="primary"
                  icon="check"
                  onClick={() => {
                    setScheduledFor(schedInput);
                    setScheduleOpen(false);
                    setJustReverified(false);
                  }}
                >
                  Confirm schedule
                </Button>
                {scheduledFor && (
                  <Button
                    sm
                    variant="ghost"
                    icon="x"
                    onClick={() => {
                      setScheduledFor(null);
                      setScheduleOpen(false);
                    }}
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'grid', gap: 16 }}>
          <div className="panel" style={{ padding: '18px 20px' }}>
            <div
              style={{
                fontFamily: 'var(--mono)',
                fontSize: 10.5,
                letterSpacing: '.08em',
                textTransform: 'uppercase',
                color: 'var(--muted)',
                marginBottom: 10,
              }}
            >
              Re-verification triggers
            </div>
            {TRIGGERS.map((t, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  gap: 9,
                  padding: '7px 0',
                  borderBottom: i < TRIGGERS.length - 1 ? '1px solid var(--rule)' : 0,
                }}
              >
                <span
                  style={{
                    width: 5,
                    height: 5,
                    borderRadius: '50%',
                    background: 'var(--ink)',
                    marginTop: 7,
                    flex: 'none',
                  }}
                />
                <span style={{ fontSize: 12.5, lineHeight: 1.45 }}>{t}</span>
              </div>
            ))}
          </div>
          <div className="panel" style={{ padding: '18px 20px' }}>
            <div
              style={{
                fontFamily: 'var(--mono)',
                fontSize: 10.5,
                letterSpacing: '.08em',
                textTransform: 'uppercase',
                color: 'var(--muted)',
                marginBottom: 12,
              }}
            >
              Tier history · audit
            </div>
            {history.map((h, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  gap: 11,
                  alignItems: 'flex-start',
                  padding: '9px 0',
                  borderBottom: i < history.length - 1 ? '1px solid var(--rule)' : 0,
                }}
              >
                <TierBadge level={h.tier} sm showLabel={false} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12.5 }}>{h.why}</div>
                  <div
                    style={{
                      fontFamily: 'var(--mono)',
                      fontSize: 11,
                      color: 'var(--muted)',
                      marginTop: 2,
                    }}
                  >
                    {h.by} · {h.at}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Screen>
  );
}
